import { AI_AUTOPILOT_USER_ID } from "@redotech/ai-autopilot-constants/constant";
import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import {
  MongoUserId,
  type MongoUserId as MongoUserIdType,
} from "@redotech/effect-api-shared/ids/mongo-ids";
import type { SupportTicketAssigneeType } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import type { AssignableUser } from "@redotech/effect-api-shared/rpc/support-ticket-related-records";
import { useHandler } from "@redotech/react-util/hook";
import { useSearch } from "@redotech/react-util/search";
import AiStarIcon from "@redotech/redo-icons/ai-star.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import type { RedoBadgeProps } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import Fuse from "fuse.js";
import { useMemo, useState } from "react";
import { useAssignSupportTicketAction } from "../actions/use-support-ticket-actions";
import {
  buildAssigneeListItems,
  getUserDisplayName,
  getUserLeadingItem,
} from "../utils/assignee-list-items";
import { useUsersForSupportTicket } from "./use-assignable-users";

/** Discriminated value type for the assignee dropdown. */
export type AssigneeDropdownValue =
  | AssignableUser
  | { readonly _tag: "ai" }
  | null;

const searcher = new Fuse<AssignableUser>([], {
  keys: ["name", "email"],
  threshold: 0.3,
});

export interface UseAssigneeSelectorResult {
  // Data
  assigneeListItems: RedoListItem<AssigneeDropdownValue>[];
  selectedItem: RedoListItem<AssigneeDropdownValue> | undefined;
  canEditAssignee: boolean;

  // Badge display
  badgeText: string;
  badgeAvatar: RedoBadgeProps["avatar"];

  // Dropdown state
  dropdownOpen: boolean;
  badgeRef: HTMLElement | null;
  setBadgeRef: (ref: HTMLElement | null) => void;

  // Handlers
  handleBadgeClick: () => void;
  handleDropdownOpenChange: (open: boolean) => void;
  handleItemSelected: (value: AssigneeDropdownValue) => void;

  // Search
  searchText: string;
  setSearchText: (text: string) => void;
}

const AI_ASSIGNEE_ITEM: RedoListItem<AssigneeDropdownValue> = {
  type: "text",
  id: "ai-autopilot",
  text: "AI Agent",
  value: { _tag: "ai" as const },
  leadingItem: { type: "icon", Icon: AiStarIcon },
};

export function useAssigneeSelector({
  supportTicketId,
  mongoAssigneeId,
  assigneeType = null,
}: {
  supportTicketId: SupportTicketId;
  mongoAssigneeId: MongoUserIdType | null;
  assigneeType?: SupportTicketAssigneeType | null;
}): UseAssigneeSelectorResult {
  const assignAction = useAssignSupportTicketAction();
  const [searchText, setSearchText] = useState("");
  const canEditAssignee = useHasPermission(SupportPermissions.EDIT_ASSIGNEE);
  // Slice the global users collection to just the ticket's team. The
  // ticket's accountId is resolved internally so callers only need to
  // pass `supportTicketId`.
  const assignableUsers = useUsersForSupportTicket(supportTicketId);
  const allUsers = useMemo(() => [...assignableUsers], [assignableUsers]);
  const filteredUsers = useSearch(searcher, allUsers, searchText);

  const assigneeListItems =
    useMemo((): RedoListItem<AssigneeDropdownValue>[] => {
      const items = buildAssigneeListItems(
        filteredUsers,
      ) as RedoListItem<AssigneeDropdownValue>[];
      // Insert AI after Unassigned (always the first item) and before users.
      return [...items.slice(0, 1), AI_ASSIGNEE_ITEM, ...items.slice(1)];
    }, [filteredUsers]);

  const selectedItem = useMemo((): RedoListItem<AssigneeDropdownValue> => {
    if (!mongoAssigneeId) {
      // No user assigned — show assigneeType label for Chat Flow
      if (assigneeType === "CHAT_FLOW") {
        return {
          type: "text",
          id: "chat-flow",
          text: "Chat Flow",
          value: null,
        };
      }
      return {
        type: "text",
        id: "unassigned",
        text: "Unassigned",
        value: null,
      };
    }
    if (MongoUserId.toRawId(mongoAssigneeId) === AI_AUTOPILOT_USER_ID) {
      return AI_ASSIGNEE_ITEM;
    }
    const currentAssignee = allUsers.find(
      (user) => user._id === mongoAssigneeId,
    );
    if (!currentAssignee) {
      return {
        type: "text",
        id: "unassigned",
        text: "Unassigned",
        value: null,
      };
    }
    const name = getUserDisplayName(currentAssignee);
    return {
      type: "text",
      id: mongoAssigneeId,
      text: name,
      value: currentAssignee,
      leadingItem: getUserLeadingItem(currentAssignee, name),
    };
  }, [mongoAssigneeId, allUsers, assigneeType]);

  // UI state for dropdown
  const [badgeRef, setBadgeRef] = useState<HTMLElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleBadgeClick = useHandler(() => {
    if (canEditAssignee) {
      setDropdownOpen((open) => !open);
    }
  });

  const handleDropdownOpenChange = useHandler((open: boolean) => {
    setDropdownOpen(open);
    if (!open) {
      setSearchText("");
    }
  });

  const handleItemSelected = useHandler((value: AssigneeDropdownValue) => {
    if (value === null) {
      void assignAction.execute(supportTicketId, {
        assignmentType: "unassign",
      });
    } else if ("_tag" in value) {
      void assignAction.execute(supportTicketId, { assignmentType: "ai" });
    } else {
      void assignAction.execute(supportTicketId, {
        assignmentType: "user",
        userId: value._id,
      });
    }
    setDropdownOpen(false);
  });

  // Extract avatar info for badge display. Narrow to TextRedoListItem since
  // all items in this selector are text items.
  const badgeAvatar: RedoBadgeProps["avatar"] =
    selectedItem.type === "text" && selectedItem.leadingItem?.type === "avatar"
      ? {
          alt: selectedItem.leadingItem.alt,
          name: selectedItem.leadingItem.name,
          imageUrl: selectedItem.leadingItem.imageUrl ?? null,
        }
      : undefined;

  return {
    // Data
    assigneeListItems,
    selectedItem,
    canEditAssignee,

    // Badge display
    badgeText: selectedItem.type === "text" ? selectedItem.text : "Unassigned",
    badgeAvatar,

    // Dropdown state
    dropdownOpen,
    badgeRef,
    setBadgeRef,

    // Handlers
    handleBadgeClick,
    handleDropdownOpenChange,
    handleItemSelected,

    // Search
    searchText,
    setSearchText,
  };
}
